import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Drawer, Grid, TextField } from '@mui/material';
import { EntitlementGroupInterface, RelationType } from '@thro-types/access-handler';

import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useEntitlementsQuery } from 'api/hooks/accessHandler';
import { useLeihbarAccessMutation } from 'api/hooks/leihbar/access';
import { SaveButton } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { entitlementGroupZodSchema } from './validation';

type EntitlementGroupPick = {
  id?: EntitlementGroupInterface['id'];
  name: EntitlementGroupInterface['name'];
  entitlements: NonNullable<EntitlementGroupInterface['entitlements']>;
};

interface Props {
  open: boolean;
  close: () => void;
  group?: EntitlementGroupInterface;
}

export const LeihbarGroupsDrawer = ({ open, close, group }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { data: entitlements, isLoading } = useEntitlementsQuery();

  const { create, update } = useLeihbarAccessMutation();

  const { handleSubmit, register, control, formState, reset } = useForm<EntitlementGroupPick>({
    resolver: zodResolver(entitlementGroupZodSchema(t)),
    values: {
      id: group?.id || undefined,
      name: group?.name || '',
      entitlements: group?.entitlements || []
    }
  });

  const handleClose = () => {
    close();
    reset();
  };

  const createEntitlementGroup = async (values: EntitlementGroupPick) => {
    if (values.id) {
      try {
        await update.mutateAsync({
          id: values.id,
          data: {
            name: values.name,
            entitlements: values.entitlements.map(ent => ent.id),
            relationType: RelationType.LEIHBAR,
            withRoleRename: true
          }
        });
        dispatch(AlertActions.success(t(translations.pages.leihbar.access.alerts.updateSuccess())));
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.access.alerts.updateFailed())));
      }
    } else {
      try {
        await create.mutateAsync({
          name: values.name,
          entitlements: values.entitlements.map(ent => ent.id),
          relationType: RelationType.LEIHBAR,
          roles: [{ name: values.name }]
        });
        dispatch(AlertActions.success(t(translations.pages.leihbar.access.alerts.createSuccess())));
      } catch (error) {
        dispatch(AlertActions.error(t(translations.pages.leihbar.access.alerts.createFailed())));
      }
    }
    handleClose();
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' }, padding: 2 }
      }}
    >
      <form onSubmit={handleSubmit(createEntitlementGroup)}>
        <Grid container flexDirection={'column'} flex={1} gap={3}>
          <TextField label={t(translations.common.name())} {...register('name')} size="small" />
          <Controller
            control={control}
            name="entitlements"
            render={({ field }) => (
              <Autocomplete
                options={entitlements?.data || []}
                getOptionLabel={ent => ent.name}
                getOptionDisabled={ent => ent.entitlementGroupId !== null}
                loading={isLoading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                multiple
                onChange={(event, value) => field.onChange(value)}
                sx={{ maxWidth: '100%' }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('Entitlements')}
                    placeholder={t('Entitlements')}
                    size="small"
                  />
                )}
                value={field.value}
                disableCloseOnSelect
              />
            )}
          />
          <Grid item alignSelf={'flex-end'} mt={'auto'}>
            <SaveButton isValid={formState.isValid} isSubmitting={formState.isSubmitting} />
          </Grid>
        </Grid>
      </form>
    </Drawer>
  );
};
